// Turn a recorded audio Blob (whatever the browser's MediaRecorder produced —
// usually webm/opus) into a 16 kHz mono 16-bit PCM WAV Blob. WAV is a format
// Gemini reliably accepts, and 16 kHz mono keeps the upload small (~1.9 MB for
// a full 60 s clip) without hurting speech transcription — the model downsamples
// to 16 kHz internally anyway.

const TARGET_SAMPLE_RATE = 16000

const encodeWav = (samples, sampleRate) => {
  // samples: Float32Array mono in [-1, 1]
  const buffer = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buffer)
  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i += 1) {
      view.setUint8(offset + i, str.charCodeAt(i))
    }
  }
  const dataLen = samples.length * 2
  writeStr(0, 'RIFF')
  view.setUint32(4, 36 + dataLen, true)
  writeStr(8, 'WAVE')
  writeStr(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM chunk size
  view.setUint16(20, 1, true) // format = PCM
  view.setUint16(22, 1, true) // channels = mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true) // byte rate
  view.setUint16(32, 2, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  writeStr(36, 'data')
  view.setUint32(40, dataLen, true)
  let offset = 44
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
    offset += 2
  }
  return new Blob([view], { type: 'audio/wav' })
}

export const blobToWav = async (blob, targetRate = TARGET_SAMPLE_RATE) => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext
  const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext
  const arrayBuffer = await blob.arrayBuffer()

  const decodeCtx = new AudioCtx()
  let decoded
  try {
    decoded = await decodeCtx.decodeAudioData(arrayBuffer)
  } finally {
    // Free the temporary context regardless of success.
    if (decodeCtx.close) decodeCtx.close()
  }

  // Resample + downmix to mono at the target rate via an offline render.
  const frames = Math.max(1, Math.ceil(decoded.duration * targetRate))
  const offline = new OfflineCtx(1, frames, targetRate)
  const source = offline.createBufferSource()
  source.buffer = decoded
  source.connect(offline.destination)
  source.start(0)
  const rendered = await offline.startRendering()
  return encodeWav(rendered.getChannelData(0), targetRate)
}
